import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import type {
  PlatformId,
  PlatformConfigurationUi,
  PlatformOptionChange,
  PlatformOptionGroup,
  PlatformOptionSelections,
} from '../../models/fundraising-comparison.models';

@Component({
  standalone: true,
  selector: 'app-platform-options',
  imports: [CommonModule],
  templateUrl: './platform-options.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlatformOptionsComponent {
  @Input({ required: true }) platformId!: PlatformId;
  @Input({ required: true }) optionGroups: PlatformOptionGroup[] = [];
  @Input({ required: true }) selections: PlatformOptionSelections = {};
  @Input({ required: true }) configurationUi!: PlatformConfigurationUi;
  @Input() showSelectedSummary = true;
  @Input() presentation: 'disclosure' | 'expanded' = 'disclosure';
  @Output() optionChange = new EventEmitter<PlatformOptionChange>();
  @Output() disclosureOpened = new EventEmitter<PlatformId>();

  select(optionGroupId: string, choiceId: string): void {
    this.optionChange.emit({
      platformId: this.platformId,
      optionGroupId,
      choiceId,
    });
  }

  updatePercentage(group: PlatformOptionGroup, event: Event): void {
    if (!group.input) return;
    const percentage = (event.target as HTMLInputElement).valueAsNumber;
    if (!Number.isFinite(percentage)) return;
    const basisPoints = Math.round(percentage * 100);
    this.select(group.id, String(basisPoints));
  }

  onDisclosureToggle(event: Event): void {
    if ((event.currentTarget as HTMLDetailsElement).open) {
      this.disclosureOpened.emit(this.platformId);
    }
  }

  inputId(optionGroupId: string, choiceId: string): string {
    return `pricing-${this.platformId}-${optionGroupId}-${choiceId}`;
  }

  selectedSummary(group: PlatformOptionGroup): string {
    if (group.input) {
      const basisPoints = Number(this.selections[group.id]);
      const label = Number.isInteger(basisPoints)
        ? `${Number((basisPoints / 100).toFixed(2))}%`
        : '';
      return group.summaryPrefix ? `${group.summaryPrefix}: ${label}` : label;
    }
    const selectedChoice = (group.choices ?? []).find(
      (choice) => choice.id === this.selections[group.id],
    );
    const label = selectedChoice?.summaryLabel ?? selectedChoice?.label ?? '';
    return group.summaryPrefix ? `${group.summaryPrefix}: ${label}` : label;
  }

  percentageValue(group: PlatformOptionGroup): number {
    return Number(this.selections[group.id] ?? group.defaultChoiceId) / 100;
  }
}
